import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { secure, withParams } from 'utils/client';
import { LIST, tags, types } from '../consts';
import { toFormData } from 'utils/helpers';

const { REACT_APP_API_URL: baseUrl } = process.env;

const supportAPI = createApi({
  reducerPath: 'support',
  tagTypes: Object.values(types),
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (build) => ({
    getTickets: build.query({
      query: (params) =>
        secure({
          method: 'GET',
          url: withParams('/customers/me/support/tickets', params),
        }),
      providesTags: [tags.SUPPORT_TICKETS(LIST)],
    }),

    setTicket: build.mutation({
      query: (body) =>
        secure({
          method: 'POST',
          url: `/support/tickets`,
          body,
        }),
      invalidatesTags: [tags.SUPPORT_TICKETS(LIST)],
    }),

    sendSupportTicketReply: build.mutation({
      query: ({ ticket_id, formData: body }) =>
        secure({
          method: 'POST',
          url: `/support/tickets/${ticket_id}/replies`,
          body,
        }),
      invalidatesTags: [tags.SUPPORT_TICKETS(LIST)],
    }),

    resolveSupportTicketReply: build.mutation({
      query: ({ ticket_id }) =>
        secure({
          method: 'PATCH',
          url: `/support/tickets/${ticket_id}/resolve`,
        }),
      invalidatesTags: [tags.SUPPORT_TICKETS(LIST)],
    }),

    reportBug: build.mutation({
      query: ({ report }) =>
        secure({
          method: 'POST',
          url: `/support/bug_report`,
          body: toFormData(report),
        }),
    }),

    postSuggestion: build.mutation({
      query: ({ suggestion }) =>
        secure({
          method: 'POST',
          url: `/support/make_suggestion`,
          body: toFormData(suggestion),
        }),
    }),

    postCuration: build.mutation({
      query: ({ suggestion }) =>
        secure({
          method: 'POST',
          url: `support/curation_request`,
          body: toFormData(suggestion),
        }),
    }),

    postPreferredPrices: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/request_preferred_pricing`,
          body,
        }),
    }),

    postDemo: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/request_demo`,
          body,
        }),
    }),

    postSupplier: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/request_supplier_account`,
          body,
        }),
    }),

    postInvoice: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/request_an_invoice`,
          body,
        }),
    }),

    postCheckOrderStatus: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/check_order_status`,
          body,
        }),
    }),

    postConfirmAvailability: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/confirm_availability`,
          body,
        }),
    }),

    postProductDocuments: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/request_product_documentation`,
          body,
        }),
    }),

    postSupportTicket: build.mutation({
      query: ({ request: body }) =>
        secure({
          method: 'POST',
          url: `support/support_ticket`,
          body,
        }),
    }),

    applyCareerJob: build.mutation({
      query: ({ form }) =>
        secure({
          method: 'POST',
          url: `support/job_apply`,
          body: toFormData(form),
        }),
    }),

    getRequestQuotes: build.query({
      query: (params) =>
        secure({
          method: 'GET',
          url: withParams('/me/quotes', params),
        }),
      providesTags: [tags.REQUEST_TICKETS(LIST)],
    }),

    deleteRequestQuote: build.mutation({
      query: (quote_request_id) =>
        secure({
          method: 'DELETE',
          url: `me/quotes/${quote_request_id}`,
        }),
      invalidatesTags: [tags.REQUEST_TICKETS(LIST)],
    }),
  }),
});

export default supportAPI;
